import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AlertProps } from 'antd';
import { persistReducer } from 'redux-persist';
import { PersistConfig } from 'redux-persist/es/types';
import storage from 'redux-persist/lib/storage';
import { RootState } from '../../../store/store';

export interface UiState {
  openSubMenu: boolean;
  toggleDrawerMenu: boolean;
  modalStates: Record<string, boolean | Record<string, unknown>>;
  alertStates: Record<string, Pick<AlertProps, 'type' | 'closable' | 'title' | 'description' | 'showIcon'>>;
}

const initialState: UiState = {
  openSubMenu: false,
  toggleDrawerMenu: false,
  modalStates: {},
  alertStates: {},
};

type RecordValue<T> = T extends Record<infer K, unknown> ? T[K] : never;

export const ui = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    openModal: (
      state,
      {
        payload,
      }: PayloadAction<
        keyof UiState['modalStates'] | { id: keyof UiState['modalStates']; props: RecordValue<UiState['modalStates']> }
      >,
    ) => {
      if (typeof payload === 'string') {
        state.modalStates[payload] = true;
        return;
      }

      state.modalStates[payload.id] = payload.props;
    },
    closeModal: (state, { payload }: PayloadAction<keyof UiState['modalStates']>) => {
      state.modalStates[payload] = false;
    },
    closeAllModals: state => {
      state.modalStates = {};
    },
    setAlert: (
      state,
      {
        payload: { id, ...payload },
      }: PayloadAction<{ id: keyof UiState['alertStates'] } & RecordValue<UiState['alertStates']>>,
    ) => {
      state.alertStates[id] = payload;
    },
    closeAlert: (state, { payload: { id } }: PayloadAction<{ id: keyof UiState['alertStates'] }>) => {
      state.alertStates[id] = {};
    },
    closeAlerts: state => {
      state.alertStates = {};
    },
    toggleSubMenu: state => {
      state.openSubMenu = !state.openSubMenu;
    },
    openSubmenu: state => {
      state.openSubMenu = true;
    },
    toggleDrawerMenu: state => {
      state.toggleDrawerMenu = !state.toggleDrawerMenu;
    },
    closeDrawerMenu: state => {
      state.toggleDrawerMenu = false;
    },
  },
});

const selectModalStates = (state: RootState) => state.ui.modalStates;

export const makeSelectModalStateById = () =>
  createSelector(
    [selectModalStates, (state, id: keyof UiState['modalStates']) => id],
    (modalStates, id) => modalStates[id] || false,
  );

const selectAlertState = (state: RootState) => state.ui.alertStates;

export const makeSelectAlertStateById = () =>
  createSelector(
    [selectAlertState, (state, id: keyof UiState['alertStates']) => id],
    (modalStates, id) => modalStates[id] || false,
  );

export const selectOpenSubMenu = (state: RootState) => state.ui.openSubMenu;
export const selectOpenDrawerMenu = (state: RootState) => state.ui.toggleDrawerMenu;

export const {
  openModal,
  closeModal,
  closeAllModals,
  toggleSubMenu,
  setAlert,
  closeAlerts,
  closeAlert,
  toggleDrawerMenu,
  openSubmenu,
  closeDrawerMenu,
} = ui.actions;

const persistConfig: PersistConfig<UiState> = {
  key: ui.name,
  storage: storage,
  version: 1,
  whitelist: ['openSubMenu'] as Array<keyof UiState>,
};

const uiReducer = persistReducer(persistConfig, ui.reducer);

export default uiReducer;
